import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const VideoPlayerContext = createContext(null);

export const useVideoPlayer = () => {
  const context = useContext(VideoPlayerContext);
  if (!context) throw new Error('useVideoPlayer must be used within VideoPlayerProvider');
  return context;
};

export const VideoPlayerProvider = ({ children }) => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);

  const isVIP = user?.vip_status?.is_active || user?.is_admin;

  // Play a video in the global context (for PiP across pages)
  const playVideo = useCallback((video, videoElement) => {
    setActiveVideo(video);
    setIsPlaying(true);
    setCurrentTime(0);
    setDuration(0);
    videoRef.current = videoElement || null;
  }, []);

  // Stop and clear the video
  const stopVideo = useCallback(() => {
    setActiveVideo(null);
    setIsPlaying(false);
    setIsMiniPlayer(false);
    setShowMiniPlayer(false);
    setCurrentTime(0);
    videoRef.current = null;
  }, []);

  // Enter mini-player mode (when scrolling past or navigating away)
  const enterMiniPlayer = useCallback(() => {
    if (!isVIP || !activeVideo) return;
    setIsMiniPlayer(true);
    setShowMiniPlayer(true);
  }, [isVIP, activeVideo]);

  // Exit mini-player and return to full view
  const exitMiniPlayer = useCallback(() => {
    setIsMiniPlayer(false);
    setShowMiniPlayer(false);
  }, []);

  // Close mini-player (stops video)
  const closeMiniPlayer = useCallback(() => {
    stopVideo();
  }, [stopVideo]);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Update time (called from the actual video element)
  const updateTime = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const updateDuration = useCallback((dur) => {
    setDuration(dur);
  }, []);

  // When user navigates away from videos page, enter mini-player for VIP
  const handlePageLeave = useCallback(() => {
    if (isVIP && activeVideo && isPlaying) {
      enterMiniPlayer();
    } else if (!isVIP) {
      stopVideo();
    }
  }, [isVIP, activeVideo, isPlaying, enterMiniPlayer, stopVideo]);

  const value = {
    activeVideo,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isMiniPlayer,
    showMiniPlayer,
    isVIP,
    videoRef,
    playVideo,
    stopVideo,
    enterMiniPlayer,
    exitMiniPlayer,
    closeMiniPlayer,
    togglePlay,
    setVolume,
    setIsMuted,
    updateTime,
    updateDuration,
    handlePageLeave,
    setIsPlaying,
    setShowMiniPlayer,
  };

  return (
    <VideoPlayerContext.Provider value={value}>
      {children}
    </VideoPlayerContext.Provider>
  );
};
